"use client";

import { useState } from "react";
import { AlertTriangle, Lock } from "lucide-react";
import { useRouter } from "next/navigation";

import { useAuth } from "@/context/auth-context";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { PasswordInput } from "@/components/password-input";
import { toast } from "@/components/ui/use-toast";

export function ForcePasswordChange(): JSX.Element | null {
  const { user } = useAuth();
  const router = useRouter();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(true);

  const needsPasswordChange = user?.needsPasswordChange;

  const handlePasswordChange = async (): Promise<void> => {
    setError("");
    
    if (!newPassword || !confirmPassword) {
      setError("Both password fields are required.");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/auth/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          currentPassword: "", // Not needed for forced password change
          newPassword,
          forceChange: true 
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Failed to change password.");
        toast({ title: "Password change failed", description: data.error || "Failed to change password.", variant: "destructive" });
      } else {
        setSuccess(true);
        toast({ title: "Password changed", description: "Your password has been updated successfully." });
        // Close the dialog immediately, then refresh session and redirect to dashboard
        setDialogOpen(false);
        setTimeout(() => {
          router.push("/employee");
        }, 1500);
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!needsPasswordChange) {
    return null;
  }

  return (
    <Dialog open={dialogOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-orange-500" />
            Change Your Password
          </DialogTitle>
          <DialogDescription>
            For security reasons, you must change your password before continuing.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {error}
            </div>
          )}

          {success && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              Password changed successfully! Redirecting...
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="newPassword">New Password</Label>
            <PasswordInput
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              disabled={loading || success}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm New Password</Label>
            <PasswordInput
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              disabled={loading || success}
            />
          </div>

          <div className="text-xs text-muted-foreground">
            Password must be at least 8 characters long.
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={handlePasswordChange}
            disabled={loading || success || !newPassword || !confirmPassword}
            className="w-full"
          >
            {loading ? "Changing Password..." : "Change Password"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
} 